// Phase 4a — `query-knowledge-graph` op.
//
// Defers a CDF instance-query into a `LazyDataset`. The op itself does
// no I/O — it validates the view + limit, splits the `:where` predicate
// into pushable + residual subtrees via `partitionFilter`, and packages
// the pushable subtree on a `QkgSource` plan source. The actual CDF
// fetch happens later, at materialization time, via the
// `EvalContext.sourceFetchers['qkg']` injected by the host.
//
// Residual predicate (the part of `:where` that can't be pushed to
// CDF) becomes a synthetic `filter` PlanNode appended to the lazy
// chain — the standard `filter` verb evaluates it client-side via
// `compileExpr` + arquero. The LLM never has to know which subtree
// pushed.

import { z } from 'zod';

import { KG_QUERY_MAX_LIMIT, SET_REF_MAX, viewToSchema } from '#core/lib/cognite/view.ts';

import { requireViews } from '../../EvalContext.ts';
import type { EvalResult } from '../../env.ts';
import { LimitTooLargeError, UnknownViewError } from '../../errors.ts';
import { type AstInput, opCallPos } from '../../normalize.ts';
import { opMeta, type SpecialCtx } from '../../op-meta.ts';
import type { LogicalPlan, PlanNode, QkgSource } from '../../plan.ts';
import { resolveSetRefsInPredicate } from '../../predicate-refs.ts';
import type { Schema } from '../../schema/types.ts';
import { LazyDataset, type Value } from '../../value.ts';
import { SORT_KEYS_DOC } from '../sort-entry.ts';
import { isRelationSubpathRef, partitionFilter } from './pushdown.ts';
import { parseQkgSort } from './sort.ts';

/**
 * `query-knowledge-graph` op. Validates the args, partitions the
 * `:where` predicate, and returns a `LazyDataset` whose source captures
 * the deferred CDF query. The source's fetcher (registered on
 * `EvalContext.sourceFetchers['qkg']`) runs when the materializer
 * forces the dataset — see `materialize.ts`.
 *
 * Special form because `:where` and `:sort` are AST nodes carrying
 * column refs that must NOT be evaluated as expressions at this layer
 * (CDF push-down needs the raw AST; residual evaluation defers to the
 * standard `filter` verb's compile path).
 *
 * A `:where` predicate may reference another bound dataset's column to
 * filter by ids it produced — `(in externalId (col eyes externalId))`,
 * or via a direct relation `(in (col ds relCol externalId) (col eyes
 * externalId))`. The shared `resolveSetRefsInPredicate` materialises
 * the referenced column and inlines its values EAGERLY here (before
 * partitioning), so the cross-dataset set is pushed to CDF (as an `in`
 * or a DMS `nested` filter) rather than requiring a join. Eager
 * resolution fully literalises the residual, so the `filter`-verb
 * replay seam is a no-op for a qkg residual.
 *
 * Single-space contract: a cross-dataset set reference is matched by
 * `externalId` alone, so the producing and consuming queries must
 * resolve to the same space (documented; not runtime-enforced).
 */
export const qkg = z
  .object({
    name: z.literal('query-knowledge-graph'),
    params: z.tuple([z.string()]),
    where: z.unknown().optional(),
    sort: z.unknown().optional(),
    limit: z.coerce.number().int().nonnegative().optional(),
  })
  .describe(
    'Query a CDF data-model view. Returns a `LazyDataset` materialized on demand. ' +
      'Positional: the view externalId. Kwargs: `:where` (s-expr predicate), `:sort` ' +
      `(vector of sort keys, each ${SORT_KEYS_DOC}), \`:limit\` (≤ ` +
      `${KG_QUERY_MAX_LIMIT}). To filter by ids from another query, bind it ` +
      'with `(dataset …)` and reference its column inside `:where` — ' +
      '`(in externalId (col other-ds externalId))`, or by a direct relation ' +
      '`(in (col ds relCol externalId) (col other-ds externalId))`; negate with ' +
      '`(not (in …))`. The referenced column is resolved + pushed to CDF (single-space ' +
      'contract: producing and consuming queries must share one space; matched by `externalId`). ' +
      `The referenced column must yield ≤ ${SET_REF_MAX} distinct values.`,
  )
  .register(opMeta, {
    evaluation: 'special',
    effect: 'io',
    example: [
      {
        expr: '(query-knowledge-graph "WellAsset" :where (= type "production") :sort [name (desc createdTime)] :limit 100)',
      },
      { expr: '(query-knowledge-graph "FlatHierarchy_Agent" :where (in externalId (col eyes externalId)) :limit 100)' },
      {
        expr: '(query-knowledge-graph "Pump" :where (in (col Pump asset externalId) (col criticalAssets externalId)) :limit 100)',
      },
    ],
    apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => {
      const pos = opCallPos(ctx.spans, ctx.path);
      const viewExternalId = data.params[0];

      // Step 1: resolve the view from `getViews()`.
      const views = requireViews(ctx.evalCtx, 'query-knowledge-graph', pos);
      const view = views.find((v) => v.externalId === viewExternalId);
      if (view === undefined) {
        throw new UnknownViewError(
          viewExternalId,
          views.map((v) => v.externalId),
          pos,
        );
      }

      // Step 2: validate `:limit` against the engine cap.
      if (data.limit !== undefined && data.limit > KG_QUERY_MAX_LIMIT) {
        throw new LimitTooLargeError(data.limit, KG_QUERY_MAX_LIMIT, pos);
      }

      // Step 2.5: validate `:sort` shape eagerly. Without this, a
      // malformed sort is only caught by the host fetcher at
      // materialize time as a raw `TypeError` — misclassified as an
      // `UnexpectedError` and surfacing seconds late behind the
      // session-fetch retry/backoff. `parseQkgSort` throws a
      // `ShapeError` here (retryable, actionable, with a SourcePos).
      if (data.sort !== undefined) {
        parseQkgSort(data.sort, pos);
      }

      // Step 3: partition the `:where` predicate. First resolve any
      // `(col other-ds column)` cross-dataset references to inline
      // literal value vectors via the shared resolver — EAGER here, so
      // the residual is fully literalised and the `filter`-verb replay
      // seam is a no-op for it.
      const schema = viewToSchema(view);
      const resolvedWhere =
        data.where === undefined
          ? undefined
          : await resolveSetRefsInPredicate(data.where as AstInput, {
              evalNode: (n) => ctx.evalNode(n, ctx.env, ctx.path),
              session: ctx.evalCtx.session,
              materialize: ctx.materialize,
              pos,
              // Leave a queried-view relation-subpath LHS — `(col
              // <view> <relCol> externalId)` — for push-down; its
              // first arg is the view, not a session binding, so
              // resolving it as a cross-dataset ref would throw a
              // spurious `UnboundSymbolError`. `partitionFilter` /
              // `relationSubpathLeaf` fold it to a DMS `nested` filter.
              skipNode: (n) => isRelationSubpathRef(n, schema),
            });
      const { pushed, residual } = partitionFilter(resolvedWhere, schema, view);

      // Step 4: package the source descriptor + plan ops.
      const source: QkgSource = {
        kind: 'qkg',
        view: viewExternalId,
        // Only include kwargs when they actually constrain — keeps the
        // materializer's per-source memo key as compact as possible so
        // identical queries share cache entries.
        ...(pushed === undefined ? {} : { where: pushed }),
        ...(data.sort === undefined ? {} : { sort: data.sort as AstInput }),
        ...(data.limit === undefined ? {} : { limit: data.limit }),
      };
      const ops: readonly PlanNode[] = residual === undefined ? [] : [buildResidualFilterNode(residual, schema)];

      const plan: LogicalPlan = { source, ops };
      const lazyDs = new LazyDataset(plan, schema);
      return { value: lazyDs satisfies Value, env: ctx.env };
    },
  });

/**
 * Wrap a residual predicate AST as a `filter` PlanNode. The node carries
 * the same `schemaAfter` as the source's input schema because `filter`
 * is schema-preserving (`passThroughSchema`).
 */
const buildResidualFilterNode = (residual: AstInput, schema: Schema): PlanNode => ({
  verb: 'filter',
  args: { name: 'filter', params: [residual] },
  schemaAfter: schema,
});
